import * as _ from "lodash";
import { call, put, select } from "redux-saga/effects";

import actions from "@/actions";

const alignTypes = {
    top: { method: "getNorth", prop: "lat", dimensionsProp: "length" },
    bottom: { method: "getSouth", prop: "lat", dimensionsProp: "length" },
    left: { method: "getWest", prop: "lng", dimensionsProp: "width" },
    right: { method: "getEast", prop: "lng", dimensionsProp: "width" },
};

const getReferenceLightByType = (selectedLights, alignTypeName) => {
    const { method } = alignTypes[alignTypeName];
    return _.reduce(selectedLights, (result, light) => {
        if (alignTypeName === "top" || alignTypeName === "right") {
            return result.bounds[method]() >= light.bounds[method]()
                ? result
                : light;
        }
        return result.bounds[method]() <= light.bounds[method]()
            ? result
            : light;
    });
};

function* validateAndApplyChanges(selectedLights, lights, prop, valueGetter) {
    let anyOverlapped = false;
    _.each(selectedLights, (light) => {
        light.point = {
            ...light.point,
            [prop]: valueGetter(light),
        };
        if (!anyOverlapped && light.isOverlapping(lights)) {
            anyOverlapped = true;
        }
    });
    if (anyOverlapped) {
        yield put(
            actions.window.showMessage(
                "We couldn't place all of the lights in your array. The skipped lights were either out of bounds of the map dimensions, or overlapped existing lights.",
                "warning",
            ),
        );
    } else {
        yield put(actions.map.values.mode.updateLights(selectedLights));
    }
}

function* alignByCardinalPoints(selectedLights, lights, alignTypeName) {
    const { method, prop, dimensionsProp } = alignTypes[alignTypeName];
    const referenceLight = getReferenceLightByType(
        selectedLights,
        alignTypeName,
        method,
    );
    const referenceValue = referenceLight.bounds[method]();
    const modifier =
        alignTypeName === "top" || alignTypeName === "right" ? -1 : 1;
    const valueGetter = (light) =>
        referenceValue +
        (light._externalDimension[dimensionsProp] / 2) * modifier;
    yield call(
        validateAndApplyChanges,
        selectedLights,
        lights,
        prop,
        valueGetter,
    );
}

function* alignByAveragePoint(selectedLights, lights, alignTypeName) {
    const props =
        alignTypeName === "middle"
            ? { minBehavior: "bottom", maxBehavior: "top" }
            : { minBehavior: "left", maxBehavior: "right" };
    const getPoint = (type) => {
        const { method } = alignTypes[type];
        return getReferenceLightByType(selectedLights, type).bounds[method]();
    };
    const maxPoint = getPoint(props.maxBehavior);
    const minPoint = getPoint(props.minBehavior);
    const centerPoint = (maxPoint + minPoint) / 2;

    const { prop } = alignTypes[props.maxBehavior];
    yield call(
        validateAndApplyChanges,
        selectedLights,
        lights,
        prop,
        () => centerPoint,
    );
}

/**
 * Aligns selected lights to a cardinal edge or average center point.
 *
 * @param root0 - The action.
 * @param root0.payload - The alignment direction (top, bottom, left, right, center, middle).
 * @yields Selects alignment data and dispatches updated light positions.
 */
function* alignLights({ payload }) {
    try {
        const { alignTypeName } = payload;
        const { lights } = yield select(({ map }) =>
            _.cloneDeep(map.values.mode.present),
        );
        const selectedLights = _.filter(lights, (light) => light.selected);
        let alignSaga = alignByCardinalPoints;
        if (!alignTypes[alignTypeName]) {
            alignSaga = alignByAveragePoint;
        }
        yield call(alignSaga, selectedLights, lights, alignTypeName);
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log("err on changing mode:", err);
    }
}

export default alignLights;
